<template>
  <v-container grid-list-xl style="max-width: 1540px">
    <v-progress-linear :active="loading" :indeterminate="loading" absolute />

    <v-form ref="form" @submit.prevent="submit" validate-on="input">
      <v-sheet elevation="2" class="pa-4">
        <v-row>
          <v-col>
            <h1>Model Detail</h1>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              :readonly="!$can('create', 'analytics')"
              v-model="analyticModel.name"
              label="Analysis Model Name"
              name="name"
              required
              :rules="[(value) => !!value || 'Required']"
              ref="modelNameInput"
              variant="outlined"
              density="compact"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="auto" md sm="12">
            <v-select
              :items="resourceTypeOptions"
              :item-value="'value'"
              :item-title="'text'"
              :readonly="!$can('delete', 'analytics')"
              :model-value="resourceType"
              label="Resource Type"
              hint="What resource type do you want to analyze"
              persistent-hint
              @update:model-value="setEndUseOptions"
              ref="resourceTypeSelect"
              variant="outlined"
              density="compact"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="auto" md sm="12">
            <v-select
              ref="analysisTypeSelect"
              :items="analysisTypes"
              :item-value="'id'"
              :item-title="'analysisTypeName'"
              :readonly="!$can('delete', 'analytics')"
              :model-value="analyticModel.analysisType.id"
              label="Select Analysis Type"
              hint="What type of analysis do you want to perform"
              persistent-hint
              @update:model-value="setAnalysisType"
              variant="outlined"
              density="compact"
            />
          </v-col>

          <v-col cols="auto" md="6" sm="12">
            <v-select
              ref="endUseSelect"
              multiple
              chips
              :items="endUseOptions"
              item-value="value"
              item-title="text"
              :readonly="!$can('delete', 'analytics')"
              v-model="selectedEndUses"
              label="Select End Use(s)"
              persistent-hint
              hint="Select end uses for analysis"
              @update:model-value="setEndUses"
              variant="outlined"
              density="compact"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="auto" md="6" sm="12">
            <v-select
              ref="factorSelect"
              multiple
              chips
              :items="factorOptions"
              item-value="value"
              item-title="text"
              v-model="selectedFactors"
              label="Select Factor(s)"
              persistent-hint
              hint="Select factors for analysis"
              @update:model-value="setFactors"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="auto" md="6" sm="12">
            <v-select
              ref="nreSelect"
              multiple
              chips
              :items="nreOptions"
              :item-title="'name'"
              :item-value="'id'"
              :model-value="analyticModel.nonRoutineEvents"
              label="Select NRE(s)"
              persistent-hint
              hint="Select NRE(s) for analysis"
              return-object
              @update:model-value="handleSetNREs"
              variant="outlined"
              density="compact"
            />
          </v-col>
        </v-row>
        <v-layout wrap justify-space-between>
          <div class="mx-2 mb-5">
            <v-list-subheader class="pl-0">
              Basis for Cooling Degree Days
            </v-list-subheader>
            <v-row>
              <v-slider
                v-model="analyticModel.degreeDaysBaseCooling"
                :min="40"
                :max="120"
                hide-details
              >
                <template v-slot:append>
                  <v-text-field
                    v-model="analyticModel.degreeDaysBaseCooling"
                    name="degree days base cooling"
                    class="mt-0"
                    hide-details
                    type="number"
                    style="width: 70px"
                    required
                    :rules="[(value) => !!value || 'Required']"
                    variant="outlined"
                    density="compact"
                  />
                </template>
              </v-slider>
            </v-row>
            <div class="text-red text-caption">
              <!-- {{ errors.first('degree days base cooling') }} -->
            </div>
          </div>

          <div class="mx-2">
            <v-list-subheader class="pl-0">
              Basis for Heating Degree Days
            </v-list-subheader>
            <v-row>
              <v-slider
                v-model="analyticModel.degreeDaysBaseHeating"
                :min="40"
                :max="120"
                hide-details
              >
                <template v-slot:append>
                  <v-text-field
                    v-model="analyticModel.degreeDaysBaseHeating"
                    name="degree days base heating"
                    class="mt-0 pt-0"
                    hide-details
                    type="number"
                    style="width: 70px"
                    required
                    :rules="[(value) => !!value || 'Required']"
                    variant="outlined"
                    density="compact"
                  />
                </template>
              </v-slider>
            </v-row>
            <div class="text-red text-caption">
              <!-- {{ errors.first('degree days base heating') }} -->
            </div>
          </div>
        </v-layout>
        <start-end-date-picker
          name="date range"
          rules="required|valid_date_range"
          :value="dateRange"
          @date-change="setDateRange"
          date-format="yyyy-MM-dd"
          ref="startEndDatePicker"
        ></start-end-date-picker>
        <v-row>
          <label class="pa-3">Comments</label>
          <v-textarea
            rows="2"
            clearable
            clear-icon="mdi-close-circle"
            auto-grow
            v-model="analyticModel.comment"
            class="pr-3"
            variant="outlined"
            density="compact"
          >
          </v-textarea>
        </v-row>
        <v-row>
          <v-col>
            <v-btn
              v-if="$can('create', 'analytics')"
              @click="validateModel"
              color="primary"
              class="mr-5"
              :loading="validating"
              :disabled="validating"
            >
              Validate
            </v-btn>
            <v-btn
              @click="downloadZip"
              title="download the raw data and results for this model in csv format"
              color="primary"
              class="mr-5"
              :disabled="!isValidModel"
            >
              Download Raw Data
            </v-btn>

            <v-btn
              v-if="$can('create', 'analytics')"
              type="submit"
              color="secondary"
              class="mr-5"
              :disabled="!isValidModel"
              :loading="loading"
            >
              Save
            </v-btn>
            <v-btn
              v-if="$can('create', 'analytics')"
              @click="saveAs"
              color="accent"
              class="mr-5"
              :disabled="!isValidModel"
            >
              Save As
            </v-btn>
            <v-btn @click="handleCancelCrud">Cancel</v-btn>
          </v-col>
        </v-row>

        <v-row justify="center">
          <stepper-validate
            :modelName="analyticModel.name"
            :factors="selectedFactors"
            :startDateTime="dateRange.startDateTime"
            :endDateTime="dateRange.endDateTime"
            :dateRange="dateRange"
            :nameValidationResult="nameValidationResult"
            :dataValidationResult="dataValidationResult"
            @fixmodel="fixModelErrors"
            ref="stepper"
          ></stepper-validate>
        </v-row>

        <v-row justify="center">
          <v-dialog v-model="showConfirm" width="500">
            <v-card>
              <v-card-title class="text-h5">
                Enter new name for model
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-text-field v-model="saveAsName"></v-text-field>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  @click="handleCancel"
                  variant="text"
                  colr="blue darken-1"
                >
                  Cancel
                </v-btn>
                <v-btn @click="handleOk" variant="text" color="blue-darken-1">
                  Ok
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-row>
      </v-sheet>
    </v-form>
  </v-container>
</template>
<style>
.v-input__icon.v-input__icon--clear {
  margin-top: 2px !important;
}
</style>
<script>
import moment from 'moment';
import StartEndDatePicker from '@/components/Fields/StartEndDatePicker';
import StepperValidate from './StepperValidate.vue';
import api from '../../analytics_models/_api';
import { apiErrorMessage } from '@/utils/api-error-message';

import { mapGetters } from 'vuex';
import { mapActions } from 'vuex';

export default {
  name: 'AnalyticsModelDetailsModule',
  components: {
    // eslint-disable-next-line vue/no-reserved-component-names
    'start-end-date-picker': StartEndDatePicker,
    'stepper-validate': StepperValidate,
  },

  data() {
    return {
      analyticModels: [],
      analyticModel: {
        id: null,
        analyticFactors: [],
        analyticType: {
          id: 1,
          analyticTypeName: 'MandV',
        },
        analysisType: {
          id: 0,
          analysisTypeName: 'Linear Regression',
        },
        degreeDaysBaseCooling: 65,
        degreeDaysBaseHeating: 65,
        startDateTime: null,
        endDateTime: null,
        nonRoutineEvents: [],
      },
      error: null,
      loading: true,
      validating: false,

      name: 'Model Detail',

      dateRange: {},
      dataValidationResult: null,
      nameValidationResult: null,

      nameIndex: 1,
      energyIndex: 2,
      weatherIndex: 3,
      factorIndex: 4,

      isValidModel: false,
      selectedSite: null,
      selectedEndUses: [],
      selectedFormula: '',
      selectedAnalysisTypeId: null,
      resourceType: '',
      selectedFactors: [],

      endUses: [],
      endUseOptions: [],
      analysisTypes: [],
      nreOptions: [],
      formulaOptions: [
        { value: '1', text: 'Production Capacity' },
        { value: '2', text: 'Adjusted Occupancy' },
      ],
      factorOptions: [],
      resourceTypeOptions: [
        { value: 'Electricity', text: 'Electricity' },
        { value: 'Gas', text: 'Gas' },
        { value: 'Water', text: 'Water' },
        { value: 'Solar', text: 'Solar' },
      ],

      startMenu: false,
      endMenu: false,
      showChart: false,
      chartOptions: {
        credits: { enabled: false },
        chart: {
          type: 'line',
          zoomType: 'x',
          borderWidth: 1,
          borderColor: '#efefef',
        },
        title: {
          text: 'Model Predictions',
        },
        subtitle: {
          text: 'R2: N/A - NMBE: N/A - CV(RMSE): N/A',
        },
        xAxis: {
          type: 'datetime',
          crosshair: true,
          labels: {
            rotation: -45,
            formatter: function () {
              if (!this.value) return '';

              return moment(new Date(this.value)).format('YYYY-M-D');
            },
          },
        },
        yAxis: {
          title: {
            text: 'kWh',
          },
        },
        tooltip: {
          pointFormat: '{point.y}',
          valueSuffix: 'kWh',
          valueDecimals: '4',
        },
        plotOptions: {
          series: {
            marker: {
              enabled: false,
              symbol: 'circle',
              radius: 2,
              states: {
                hover: {
                  enabled: true,
                },
              },
            },
          },
        },
        series: [{}],
      },

      defaultOptions: {},
      showConfirm: false,
      saveAsName: null,
    };
  },

  async beforeCreate() {},

  async mounted() {
    await this.getModel();
  },

  async created() {
    await this.getFactors();
    await this.getAnalysisTypes();

    // this.$validator.attach;
  },

  computed: {
    ...mapGetters('session', ['jwtName', 'jwtPicture']),
  },

  methods: {
    ...mapActions({}),

    getAvatar() {
      return this.jwtPicture;
    },

    setFormula() {},

    setDateRange(e) {
      // The picker emits raw JS Date / luxon DateTime values; normalize to
      // yyyy-MM-dd so the API and the model get a clean date, not a
      // timezone-stamped datetime string.
      const startDateTime = this.formatToDateString(e.startDateTime);
      const endDateTime = this.formatToDateString(e.endDateTime);

      this.analyticModel.startDateTime = startDateTime;
      this.analyticModel.endDateTime = endDateTime;

      // Keep dateRange in sync — validateModel reads dateRange for the
      // validation call, and the picker's v-model doesn't write back to it.
      this.dateRange = { startDateTime, endDateTime };
    },

    formatToDateString(value) {
      if (!value) return value;
      // luxon DateTime exposes toISODate(); everything else moment can parse.
      if (typeof value === 'object' && typeof value.toISODate === 'function') {
        return value.toISODate();
      }
      // For date-only or ISO strings, keep the literal calendar date instead
      // of letting moment shift it across timezones (a trailing Z would push
      // the day backwards in timezones behind UTC).
      if (typeof value === 'string') {
        const match = value.match(/^\d{4}-\d{2}-\d{2}/);
        if (match) return match[0];
      }
      return moment(value).format('YYYY-MM-DD');
    },

    saveAs() {
      this.showConfirm = true;
      this.saveAsName = this.analyticModel.name + ' (copy)';
    },
    async handleOk() {
      this.showConfirm = false;
      this.analyticModel.id = null;
      this.analyticModel.name = this.saveAsName;
      this.postModel();
    },
    handleCancel() {
      this.saveAsName = this.analyticModel.name;
      this.showConfirm = false;
    },

    async postModel() {
      await api
        .createAnalyticModel(this.selectedSite, this.analyticModel)
        .then(() => {
          const options = {
            title: 'Success',
            message: 'Successfully saved as model',
            status: 'success',
          };
          this.$toast.show(options);
          this.$router.push({ name: 'Models' });
        })
        .catch((error) => {
          return Promise.reject(error);
        })
        .finally(() => (this.loading = false));
    },

    async submit(event) {
      const { valid } = await event;
      if (!valid) {
        return Promise.reject({
          Error: 'Please fill in all required fields',
        });
      }
      this.putModel();
    },

    async getModel() {
      await api
        .getAnalyticModel(this.$route.params.id)
        .then((response) => {
          this.analyticModel = response;
          this.selectedSite = this.analyticModel.siteId;

          if (this.analyticModel.endUses.length > 0) {
            const endUse = this.analyticModel.endUses[0];
            this.resourceType = endUse.resourceType;
          } else {
            this.resourceType = 'Electricity';
          }

          this.dateRange = {
            startDateTime: this.formatToDateString(
              this.analyticModel.startDateTime
            ),
            endDateTime: this.formatToDateString(
              this.analyticModel.endDateTime
            ),
          };
          this.selectedFactors = this.mapanalyticFactors(
            this.analyticModel.analyticFactors
          );
          //repopulate the end use lookup
          this.getEndUses();
          this.getNREs();
          //clear success styling after loading valid data
          requestAnimationFrame(() => {
            // this.$refs.form.reset();
          });

          this.isValidModel = this.checkValidModel();
        })
        .catch((error) => {
          return Promise.reject(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async putModel() {
      await api
        .updateAnalyticModel(this.selectedSite, this.analyticModel)
        .then(() => {
          const options = {
            title: 'Success',
            message: 'Successfully updated model',
            status: 'success',
          };
          this.$toast.show(options);
          this.$router.push({ name: 'Models' });
        })
        .catch((error) => {
          return Promise.reject(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    //ENDUSES - START
    async getEndUses() {
      await api
        .getEndUses(this.selectedSite)
        .then((response) => {
          this.endUses = response;
          this.endUseOptions = this.mapEndUseLookup(
            this.endUses,
            this.resourceType
          );
          this.selectedEndUses = this.mapAnalyticsEndUses(
            this.analyticModel.endUses
          );
        })
        .catch((error) => {
          return Promise.reject(error);
        })
        .finally(() => (this.loading = false));
    },

    setEndUseOptions(selectedResource) {
      this.endUseOptions = this.mapEndUseLookup(this.endUses, selectedResource);
    },

    mapEndUseLookup(endUses, resourceTypeName) {
      const makeOptionItems = (item) => {
        if (item.resourceType === resourceTypeName) {
          return {
            value: item.endUseId,
            text: item.endUseName,
            endUseUnitOfMeasureName: item.endUseUnitOfMeasureName,
            resourceType: item.resourceType,
          };
        }
      };

      const eul = endUses.map(makeOptionItems);
      return eul.filter((e) => {
        return e != undefined;
      });
    },

    setEndUses(item) {
      this.analyticModel.endUses = this.mapSelectedEndUses(item);
    },

    mapSelectedEndUses(endUses) {
      const makeEndUse = (selectedItem) => {
        const selectedEndUseOption = this.endUseOptions.find(
          (o) => o.value === selectedItem
        );

        return {
          endUseId: selectedItem,
          endUseName: selectedEndUseOption.text,
          resourceType: 'Electricity', //get this value from the resource type selection
          endUseUnitOfMeasureName: selectedEndUseOption.endUseUnitOfMeasureName,
          selected: true,
          siteId: this.selectedSite, //temporary
        };
      };

      const eu = endUses.map(makeEndUse);
      return eu;
    },

    mapAnalyticsEndUses(enduses) {
      const extractEndUse = (item) => {
        const endUseToExtract = this.endUseOptions.find(
          (o) => o.text === item.endUseName
        );
        return endUseToExtract.value;
      };

      const af = enduses.map(extractEndUse);
      return af;
    },
    //ENDUSES - END

    //TYPE - START
    async getAnalysisTypes() {
      await api
        .getAnalysisTypes()
        .then((response) => {
          this.analysisTypes = response;
        })
        .catch((error) => {
          return Promise.reject(error);
        })
        .finally(() => (this.loading = false));
    },

    setAnalysisType(item) {
      if (this.analysisTypes.length > 0) {
        this.analyticModel.analysisType = {
          id: item,
          analysisTypeName: this.analysisTypes.find((o) => o.id === item).text,
        };
      }
    },
    //TYPE - END

    //FACTORS - START
    async getFactors() {
      this.loading = true;
      try {
        const response = await api.getAnalyticFactors();
        this.factorOptions = this.mapFactorLookup(response);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        this.loading = false;
      }
    },

    mapFactorLookup(endUses) {
      const makeOptionItems = (item) => {
        return {
          value: item.id,
          text: item.factorName,
          analyticFactorName: item.analyticFactorName,
        };
      };
      const fl = endUses.map(makeOptionItems);
      return fl;
    },

    setFactors(items) {
      this.analyticModel.analyticFactors = this.mapSelectedFactors(items);
    },

    mapSelectedFactors(factors) {
      const makeFactor = (selectedItem) => {
        const selectedFactorOption = this.factorOptions.find(
          (o) => o.value === selectedItem
        );
        return {
          id: selectedItem,
          analyticFactorName: selectedFactorOption.analyticFactorName,
          factorName: selectedFactorOption.text,
          selected: true,
        };
      };

      const sf = factors.map(makeFactor);
      return sf;
    },

    mapanalyticFactors(factors) {
      const extractFactor = (item) => {
        const factorToExtract = this.factorOptions.find(
          (o) => o.analyticFactorName == item.analyticFactorName
        );
        return factorToExtract?.value;
      };

      const af = factors.map(extractFactor);
      return af;
    },
    //FACTORS - END

    //NRES - START
    async getNREs() {
      api
        .getNREs(this.selectedSite)
        .then((response) => {
          this.nreOptions = response;
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    },

    handleSetNREs(selectedNREs) {
      this.analyticModel.nonRoutineEvents = selectedNREs.map((x) => {
        return { ...x, selected: true };
      });
    },
    //NRES -END

    mapChartSeriesData(predictions) {
      const makeSeriesDataPoint = (item, property) => {
        const unixDate = moment(item.date).valueOf();
        const value = item[property];
        return [unixDate, value];
      };
      const seriesData = [
        {
          name: 'Actual',
          data: predictions.map((p) => makeSeriesDataPoint(p, 'kWh')),
        },
        {
          name: 'Predicted',
          data: predictions.map((a) => makeSeriesDataPoint(a, 'prediction')),
        },
      ];
      return seriesData;
    },

    checkValidModel() {
      this.isValidModelName = this.analyticModel.name !== null;
      this.isValidSite = this.analyticModel.siteId !== null;
      this.isValidResourceType = this.resourceType !== null;
      this.isValidAnalysisType = this.analyticModel.analysisType !== null;
      this.isValidEndUse = this.analyticModel.endUses.length > 0;
      this.isValidFactor = true;
      this.isValidDateRange =
        this.analyticModel.startDateTime < this.analyticModel.endDateTime;

      const isValid =
        this.isValidModelName &&
        this.isValidSite &&
        this.isValidResourceType &&
        this.isValidAnalysisType &&
        this.isValidEndUse &&
        this.isValidFactor &&
        this.isValidDateRange;
      return isValid;
    },

    ///Validate Dialog
    async validateModel() {
      const { valid } = await this.$refs.form.validate();

      if (!valid) {
        return;
      }

      this.validating = true;
      try {
        this.nameValidationResult =
          await api.validateAnalyticModelNameForUpdate(
            this.analyticModel.siteId,
            this.analyticModel.id,
            this.analyticModel.name
          );

        this.dataValidationResult = await api.validateAnalyticModelDataForRange(
          this.analyticModel.id,
          this.dateRange.startDateTime,
          this.dateRange.endDateTime
        );

        this.isValidFactor = this.selectedFactors.length > 0;
        this.isValidModel =
          this.dataValidationResult.validation &&
          this.nameValidationResult &&
          this.isValidFactor;

        // Only open the stepper dialog once the validation calls succeed.
        this.$refs.stepper.validateModel();
      } catch (error) {
        console.error(error);
      } finally {
        this.validating = false;
      }
    },

    fixModelErrors(validationItem) {
      let step = validationItem.item;
      let newDateRange = validationItem.newDateRange;

      if (step === this.energyIndex || step === this.weatherIndex) {
        this.dateRange = newDateRange;
      }

      // Focus after the dialog has finished closing. The dialog restores
      // focus to the Validate button when it closes, which would override
      // an earlier focus() call.
      setTimeout(() => {
        switch (step) {
          case this.nameIndex:
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
            this.$refs.modelNameInput?.focus();
            break;
          case this.factorIndex:
            this.$refs.factorSelect?.focus();
            break;
          case this.weatherIndex:
          case this.energyIndex:
            this.$refs.startEndDatePicker?.$refs?.startPicker?.focus();
            break;
          default:
            break;
        }
      }, 300);

      this.isValidModel = false;
    },

    async downloadZip() {
      try {
        this.loading = true;
        const zipStream = await api.downLoadAnalyticModelZip(
          this.selectedSite,
          this.analyticModel
        );
        const fileURL = window.URL.createObjectURL(new Blob([zipStream]));
        const fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('download', this.analyticModel.name + '.zip');
        document.body.appendChild(fileLink);

        fileLink.click();
        fileLink.remove();
        window.URL.revokeObjectURL(fileURL);
      } catch (error) {
        this.$toast.show({
          title: 'Download failed',
          message: await apiErrorMessage(error),
          status: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    handleCancelCrud() {
      this.$router.push({ name: 'Models' });
    },
  },
};
</script>
